/**
 * Client-side OTP lockout: consecutive failed verify attempts and escalating
 * cooldown before the next "Send OTP" (does not block verify attempts).
 */

import { normalizeIndianMobileDigits } from './indianMobile';

const PREFIX = 'daiv_otp_';

export const MAX_INVALID_OTP_STREAK = 5;
/** First lockout cooldown after 5 failures (seconds); doubles each further lockout. */
export const BASE_OTP_SEND_COOLDOWN_SEC = 30;

function norm(m) {
  return normalizeIndianMobileDigits(m);
}

function defaultState() {
  return { streak: 0, strikeLevel: 0, cooldownUntil: 0 };
}

function readState(mobile) {
  const m = norm(mobile);
  if (m.length !== 10) return defaultState();
  try {
    const raw = localStorage.getItem(`${PREFIX}${m}`);
    if (!raw) return defaultState();
    const parsed = JSON.parse(raw);
    return {
      streak: Number(parsed.streak) || 0,
      strikeLevel: Number(parsed.strikeLevel) || 0,
      cooldownUntil: Number(parsed.cooldownUntil) || 0,
    };
  } catch {
    return defaultState();
  }
}

function writeState(mobile, state) {
  const m = norm(mobile);
  if (m.length !== 10) return;
  localStorage.setItem(`${PREFIX}${m}`, JSON.stringify(state));
}

/**
 * @returns {number} seconds until send OTP is allowed (0 if none)
 */
export function getSendOtpCooldownRemainingSec(mobileNumber) {
  const { cooldownUntil } = readState(mobileNumber);
  if (!cooldownUntil) return 0;
  const rem = Math.ceil((cooldownUntil - Date.now()) / 1000);
  return rem > 0 ? rem : 0;
}

/**
 * Call after a new OTP is sent successfully — resets consecutive wrong-try count for this code
 * (keeps strike level / send cooldown). So the first wrong attempt shows 1/5, second 2/5, etc.
 */
export function resetOtpStreakForNewOtp(mobileNumber) {
  const m = norm(mobileNumber);
  if (m.length !== 10) return;
  const s = readState(mobileNumber);
  writeState(m, { ...s, streak: 0 });
}

/**
 * Call after a successful OTP verification — clears lockout for this number.
 */
export function recordOtpVerifySuccess(mobileNumber) {
  const m = norm(mobileNumber);
  if (m.length !== 10) return;
  localStorage.removeItem(`${PREFIX}${m}`);
}

/**
 * Call after a failed OTP verification.
 * @returns {{ triggered: false, streak: number } | { triggered: true, streak: number, strikeLevel: number, cooldownSec: number, redirectTo: string }}
 */
export function recordOtpVerifyFailure(mobileNumber) {
  const m = norm(mobileNumber);
  if (m.length !== 10) return { triggered: false, streak: 0 };

  const prev = readState(m);
  const streak = prev.streak + 1;
  let { strikeLevel, cooldownUntil } = prev;

  if (streak < MAX_INVALID_OTP_STREAK) {
    writeState(m, { streak, strikeLevel, cooldownUntil });
    return { triggered: false, streak };
  }

  strikeLevel += 1;
  const cooldownSec = BASE_OTP_SEND_COOLDOWN_SEC * 2 ** (strikeLevel - 1);
  cooldownUntil = Date.now() + cooldownSec * 1000;
  /** Caller should show mobile entry (e.g. login drawer) — cooldown shows on Send OTP, not on OTP step. */
  const redirectTo = '/';

  writeState(m, { streak: 0, strikeLevel, cooldownUntil });
  return { triggered: true, streak: MAX_INVALID_OTP_STREAK, strikeLevel, cooldownSec, redirectTo };
}
